// Display metadata for profiles.creator_tags role values, shared by every
// surface that renders the creator role pill (user profile, storefront,
// account menu). The embed script (app/api/embed/[slug]/route.ts) runs as
// plain JS in a sandboxed iframe and can't import this, so it carries its own
// copy of the same labels/colours — keep the two in sync.
//
// scriptor->Scriptors, creator->Creators, artist_2d->2D Artist,
// artist_3d->3D Artist, va->Voice Actors, sd->Sound Designers, editor->Editors,
// developer->Game Devs are each synced to their own Discord role in
// /api/account/creator-mode. The two colour palettes no longer match exactly:
// Discord's roles run a cyan-to-aquamarine gradient by tier (2026-09-21) while
// these pills keep their original distinct hues, on purpose — Discord needed
// every role family to read as one glance-able group, the app pill's job is to
// tell individual roles apart, not to group them.
//
// The plain "artist" tag/role was removed 2026-09-21: it was legacy-only (the
// picker only offers artist_2d/artist_3d), had zero holders in Discord or the
// DB, and was redundant with those two.

export const ROLE_TAG_LABELS: Record<string, string> = {
  scriptor: "Scriptor",
  artist_2d: "2D Artist",
  artist_3d: "3D Artist",
  va: "VA",
  sd: "SFXD",
  editor: "Editor",
  developer: "Developer",
  creator: "Creator",
}

// Older/longer spellings of a role that can end up stored (hand-edited rows,
// early builds). The picker and /api/account/creator-mode only ever write the
// short keys above, but display and validation both run values through this so
// a legacy "sound_designer" renders as "SFXD" instead of the raw string.
export const ROLE_TAG_ALIASES: Record<string, string> = {
  sound_designer: "sd",
  voice_actor: "va",
  video_editor: "editor",
  game_dev: "developer",
  game_developer: "developer",
}

export function normalizeRoleTag(tag: string): string {
  const key = tag.trim().toLowerCase()
  return ROLE_TAG_ALIASES[key] ?? key
}

// "some_unknown_role" -> "Some Unknown Role": a last-resort label so an
// unrecognised value never shows up as a raw snake_case identifier.
function humanizeRoleTag(tag: string): string {
  return tag.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim()
}

export function roleTagLabel(tag: string): string {
  const key = normalizeRoleTag(tag)
  return ROLE_TAG_LABELS[key] ?? humanizeRoleTag(key)
}

export function roleTagClasses(tag: string): string {
  return ROLE_TAG_CLASSES[normalizeRoleTag(tag)] ?? ""
}

// Exact hex values on purpose: this app's theme remaps Tailwind's blue-*/violet-*
// to its brand purple (both resolve to hue ~286), so named blue and violet
// classes rendered as the same purple. Hex keeps every role distinct, and
// matches the embed script's palette exactly.
export const ROLE_TAG_CLASSES: Record<string, string> = {
  scriptor: "bg-[#3b82f6] text-white border-[#2563eb]",
  creator: "bg-[#10b981] text-white border-[#059669]",
  artist_2d: "bg-[#f97316] text-black border-[#ea580c]",
  artist_3d: "bg-[#0d9488] text-white border-[#0f766e]",
  va: "bg-[#f9a8d4] text-black border-[#ec4899]",
  sd: "bg-[#facc15] text-black border-[#eab308]",
  editor: "bg-[#0ea5e9] text-black border-[#0284c7]",
  developer: "bg-[#84cc16] text-black border-[#65a30d]",
}

// Founder is a one-time purchase and Founder+ is a Founder who also holds an
// active paid subscription — both are account statuses, not creator roles, so
// they get their own gold styles (gradients on the site; Discord can't do
// gradient role colours) instead of borrowing a role colour.
export const FOUNDER_BADGE_CLASSES =
  "bg-gradient-to-r from-[#fcd34d] to-[#f59e0b] text-black border-[#f59e0b]"
export const FOUNDER_PLUS_BADGE_CLASSES =
  "bg-gradient-to-r from-[#fbbf24] via-[#d946ef] to-[#7c3aed] text-white border-[#d946ef]"
